import { ElementProps } from '@/components/sections/builder/types';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';

import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { Switch } from '@/components/ui/switch';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';
import { defaultDataSearchPage } from '@/components/sections/builder/elements/body/search';
import { SearchPageElementDataType } from '@/components/sections/builder/elements/body/search/type';

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as SearchPageElementDataType;

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Layout Settings</h1>
      <h4 className='text-xs'>Product per page</h4>
      <InputSlider
        value={data.limit ?? (defaultDataSearchPage.limit as number)}
        max={25}
        onChange={(val) =>
          builder.setElementData(element.id, {
            limit: val,
          })
        }
      />
      <h4 className='mt-4 text-xs'>Number of columns on desktop</h4>
      <InputSlider
        value={data.totalCol ?? defaultDataSearchPage.totalCol}
        min={1}
        max={5}
        onChange={(val) =>
          builder.setElementData(element.id, {
            totalCol: val,
          })
        }
      />
      <h4 className='mt-4 text-xs'>Number of columns on mobile</h4>
      <InputSlider
        value={data.totalColMobile ?? defaultDataSearchPage.totalColMobile}
        min={1}
        max={3}
        onChange={(val) =>
          builder.setElementData(element.id, {
            totalColMobile: val,
          })
        }
      />
      <div className='mt-4 flex items-center gap-2'>
        <label htmlFor='enableQuickAddButton'>Enable quick add button</label>
        <Switch
          id='enableQuickAddButton'
          aria-label='Select'
          className='ml-auto'
          checked={data.enableQuickAddButton ?? false}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableQuickAddButton: !!checked,
            });
          }}
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mt-2 text-sm font-bold'>Product Card</h1>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Image ratio</label>
        <InputSelect
          defaultValue={data.imageRatio ?? defaultDataSearchPage.imageRatio}
          options={[
            { label: 'Adapt to image', value: 'adaptToImage' },
            { label: 'Portrait', value: 'portrait' },
            { label: 'Square', value: 'square' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              imageRatio: v,
            })
          }
        />
      </div>
      <div className='mt-4 flex gap-x-1'>
        <label htmlFor='showImage'>Show image</label>
        <Switch
          id='showImage'
          aria-label='Select'
          className='ml-auto'
          checked={data.showImage ?? defaultDataSearchPage.showImage}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showImage: !!checked,
            });
          }}
        />
      </div>
      <div className='mt-4 flex gap-x-1'>
        <label htmlFor='showSecondImageOnHover'>Show second image on hover</label>
        <Switch
          id='showSecondImageOnHover'
          aria-label='Select'
          className='ml-auto'
          checked={data.showSecondImageOnHover ?? false}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showSecondImageOnHover: !!checked,
            });
          }}
        />
      </div>
      <div className='mt-4 flex gap-x-1'>
        <label htmlFor='showShadow'>Show shadow</label>
        <Switch
          id='showShadow'
          aria-label='Select'
          className='ml-auto'
          checked={data.showShadow ?? false}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showShadow: !!checked,
            });
          }}
        />
      </div>
      <div className='mt-4 flex items-center gap-2'>
        <label htmlFor='showCategory'>Show category</label>
        <Switch
          id='showCategory'
          aria-label='Select'
          className='ml-auto'
          checked={data.showCategory ?? defaultDataSearchPage.showCategory}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showCategory: !!checked,
            });
          }}
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mb-3 text-sm font-bold'>Filter and sorting</h1>
      <div className='mt-4 flex items-center gap-2'>
        <label htmlFor='enableFilter'>Enable filter</label>
        <Switch
          id='enableFilter'
          aria-label='Select'
          className='ml-auto'
          checked={data.enableFilter ?? defaultDataSearchPage.enableFilter}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableFilter: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mt-4 flex items-center gap-2'>
        <label htmlFor='enableSort'>Enable sort</label>
        <Switch
          id='enableSort'
          aria-label='Select'
          className='ml-auto'
          checked={data.enableSort ?? defaultDataSearchPage.enableSort}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableSort: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      {data.enableFilter && (
        <div className='my-4 grid grid-cols-2 items-center gap-2'>
          <label className='text-xs'>Dekstop filter layout</label>
          <InputSelect
            defaultValue={data.filterLayout ?? defaultDataSearchPage.filterLayout}
            options={[
              { label: 'Horizontal', value: 'horizontal' },
              { label: 'Vertical', value: 'vertical' },
              { label: 'Drawer', value: 'drawer' },
            ]}
            onChange={(v) =>
              builder.setElementData(element.id, {
                filterLayout: v,
              })
            }
          />
        </div>
      )}
      {data.enableFilter && (
        <>
          <div className='mt-4 flex items-center gap-2'>
            <label htmlFor='showFilterAvailability'>Show filter availability</label>
            <Switch
              id='showFilterAvailability'
              aria-label='Select'
              className='ml-auto'
              checked={data.showFilterAvailability ?? defaultDataSearchPage.showFilterAvailability}
              onCheckedChange={(checked) => {
                builder.setElementData(element.id, {
                  showFilterAvailability: !!checked,
                });
              }}
            />
          </div>
          <div className='mt-4 flex items-center gap-2'>
            <label htmlFor='showFilterPrice'>Show filter price</label>
            <Switch
              id='showFilterPrice'
              aria-label='Select'
              className='ml-auto'
              checked={data.showFilterPrice ?? defaultDataSearchPage.showFilterPrice}
              onCheckedChange={(checked) => {
                builder.setElementData(element.id, {
                  showFilterPrice: !!checked,
                });
              }}
            />
          </div>
        </>
      )}
      <div className='mt-4 flex items-center gap-2'>
        <label htmlFor='showTotalProduct'>Show total product</label>
        <Switch
          id='showTotalProduct'
          aria-label='Select'
          className='ml-auto'
          checked={data.showTotalProduct ?? defaultDataSearchPage.showTotalProduct}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showTotalProduct: !!checked,
            });
          }}
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <h4 className='mt-4'>Color scheme</h4>
      <SchemeSetting
        value={data.colorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorScheme: theme,
          });
        }}
      />

      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
    </div>
  );
}
